import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { SignedIn, SignedOut, SignInButton, useUser, useAuth } from '@clerk/clerk-react';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useUserData } from '../../hooks/useUserData';
import { buildCheckoutUrl, getRevenueCatManagementUrl } from '../../utils/billing';
import { AUTH_ENDPOINTS } from '../../config/api';
import { useQueryClient } from '@tanstack/react-query';
import { userDataQueryKey } from '../../hooks/useUserData';
import PaywallModal from '../common/PaywallModal';

const formatDateTime = (value?: number | Date | null): string => {
  if (!value) return '—';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MyAccount = (): JSX.Element => {
  const { isLoaded, user } = useUser();
  const {
    data: userData,
    error: userDataError,
    isLoading: isUserDataLoading,
    isFetching: isUserDataFetching,
  } = useUserData();
  const loadingUserData = isUserDataLoading || (isUserDataFetching && !userData);
  const userDataErrorMessage = userDataError ? userDataError.message : null;
  const [managementUrl, setManagementUrl] = useState<string | null>(null);
  const [loadingManagement, setLoadingManagement] = useState(false);
  const [managementError, setManagementError] = useState<string | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const fetchManagementUrl = async () => {
      if (!user || !userData?.pro) {
        if (!cancelled) {
          setManagementUrl(null);
          setManagementError(null);
        }
        return;
      }

      const revenueCatApiKey = import.meta.env.RC_WEB_SDK_KEY;
      if (!revenueCatApiKey) {
        if (!cancelled) {
          setManagementUrl(null);
          setManagementError('Subscription management is not yet configured.');
        }
        return;
      }

      if (!userData.billingId) {
        if (!cancelled) {
          setManagementUrl(null);
          setManagementError('Subscription details are incomplete.');
        }
        return;
      }

      try {
        setLoadingManagement(true);
        setManagementError(null);

        const url = await getRevenueCatManagementUrl(revenueCatApiKey, userData.billingId);
        if (!cancelled) {
          setManagementUrl(url);
          if (!url) {
            setManagementError('Unable to find your subscription portal.');
          }
        }
      } catch (error) {
        console.error('Failed to load RevenueCat management URL:', error);
        if (!cancelled) {
          setManagementUrl(null);
          setManagementError(
            error instanceof Error
              ? error.message
              : 'Unable to load your subscription portal.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingManagement(false);
        }
      }
    };

    void fetchManagementUrl();

    return () => {
      cancelled = true;
    };
  }, [user, userData?.billingId, userData?.pro]);

  const hasPro = Boolean(userData?.pro);
  const checkoutUrl = buildCheckoutUrl(userData?.billingId);
  const subscriptionButtonDisabled = hasPro
    ? loadingManagement || !managementUrl
    : loadingUserData || !checkoutUrl;

  const subscriptionHelperText = hasPro
    ? managementError
      ? managementError
      : loadingManagement
        ? 'Preparing your subscription portal…'
        : managementUrl
          ? 'Open the RevenueCat portal to manage your plan.'
          : 'Subscription management will be available shortly.'
    : userDataErrorMessage
      ? userDataErrorMessage
      : loadingUserData
        ? 'Preparing your upgrade link…'
        : checkoutUrl
          ? 'You will be redirected to secure checkout.'
          : 'Generating your checkout link…';

  const handleSubscriptionAction = () => {
    if (hasPro) {
      if (managementUrl) {
        window.open(managementUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    setPaywallOpen(true);
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(AUTH_ENDPOINTS.GOOGLE_DISCONNECT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect Google account');
      }

      await queryClient.invalidateQueries({ queryKey: userDataQueryKey });
      setDisconnectDialogOpen(false);
    } catch (error) {
      console.error('Failed to disconnect:', error);
      // Ideally show error toast here
    } finally {
      setDisconnecting(false);
    }
  };

  const isGoogleConnected = Boolean(userData?.isGoogleConnected);

  const subscriptionChipProps = (() => {
    if (userDataError) {
      return { label: 'Subscription unavailable', color: 'error' as const, variant: 'outlined' as const };
    }

    if (loadingUserData) {
      return { label: 'Checking plan…', color: 'primary' as const, variant: 'outlined' as const };
    }

    if (hasPro) {
      return { label: 'Pro member', color: 'primary' as const, variant: 'filled' as const };
    }

    return { label: 'Free plan', color: 'default' as const, variant: 'outlined' as const };
  })();

  const secondaryEmails = useMemo(() => {
    if (!user?.emailAddresses?.length) return [];

    return user.emailAddresses.filter((email) => email.id !== user.primaryEmailAddressId);
  }, [user?.emailAddresses, user?.primaryEmailAddressId]);

  if (!isLoaded) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="primary" />
            <Typography variant="body1" color="text.secondary">
              Loading your account information...
            </Typography>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
              Sign in to view your account
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Access personalised information and manage your subscription by signing in to your account.
            </Typography>
            <SignInButton mode="modal">
              <Button variant="contained" color="primary">
                Sign In
              </Button>
            </SignInButton>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: 'rgba(0,0,0,0.02)' }}>
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 4, md: 6 },
            backgroundColor: 'background.paper',
            boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
          }}
        >
          <Stack spacing={3} alignItems="center" textAlign="center" sx={{ mb: 2 }}>
            <div>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                My Account
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage your personal information and subscription details
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                <Chip
                  label={subscriptionChipProps.label}
                  color={subscriptionChipProps.color}
                  variant={subscriptionChipProps.variant}
                  sx={{ borderRadius: 2 }}
                />
              </Stack>
            </div>
            <Stack spacing={1} alignItems="center" sx={{ width: '100%', maxWidth: 320 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ py: 1.5, borderRadius: 2 }}
                disabled={subscriptionButtonDisabled}
                onClick={handleSubscriptionAction}
              >
                {hasPro
                  ? loadingManagement
                    ? 'Loading…'
                    : 'Manage Subscription'
                  : loadingUserData
                    ? 'Preparing…'
                    : 'Upgrade to Premium'}
              </Button>
              <Typography
                variant="caption"
                color={subscriptionHelperText && (managementError || userDataError) ? 'error' : 'text.secondary'}
              >
                {subscriptionHelperText}
              </Typography>
            </Stack>
          </Stack>

          {isGoogleConnected ? (
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDisconnectDialogOpen(true)}
              >
                Disconnect Google Account
              </Button>
            </Box>
          ) : (
            <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Button color="warning" variant="contained" href={AUTH_ENDPOINTS.GOOGLE_LOGIN}>
              Connect Google Account
            </Button>
            </Box>
          )}

          <Divider sx={{ my: { xs: 3, md: 4 } }} />

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Primary Email
                  </Typography>
                  <Typography variant="h6">{user.primaryEmailAddress?.emailAddress ?? '—'}</Typography>
                </Box>

                {secondaryEmails.length > 0 && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Additional Emails
                    </Typography>
                    <List dense disablePadding>
                      {secondaryEmails.map((email) => (
                        <ListItem key={email.id} disableGutters sx={{ py: 0.5 }}>
                          <ListItemText primary={email.emailAddress} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                <Box>
                  <Typography variant="overline" color="text.secondary">
                    User ID
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {user.id}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="h6">{formatDateTime(user.createdAt)}</Typography>
                </Box>

                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Last Sign In
                  </Typography>
                  <Typography variant="h6">{formatDateTime(user.lastSignInAt)}</Typography>
                </Box>

                {user.publicMetadata && Object.keys(user.publicMetadata).length > 0 && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Public Metadata
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {Object.entries(user.publicMetadata).map(([key, value]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`}
                          color="primary"
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Dialog
        open={disconnectDialogOpen}
        onClose={() => !disconnecting && setDisconnectDialogOpen(false)}
      >
        <DialogTitle>Disconnect Google Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will prevent GroupMyChannels from obtaining any updates to your YouTube data. You can reconnect at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDisconnectDialogOpen(false)} 
            disabled={disconnecting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDisconnect} 
            color="error" 
            variant="contained"
            disabled={disconnecting}
            autoFocus
          >
            {disconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        checkoutUrl={checkoutUrl}
        loading={loadingUserData}
        error={userDataErrorMessage}
      />
    </Box>
  );
};

const MyAccountPage = (): JSX.Element => {
  return (
    <>
      <SignedIn>
        <MyAccount />
      </SignedIn>
      <SignedOut>
        <MyAccount />
      </SignedOut>
    </>
  );
};

export default MyAccountPage;
